import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../../../utils/api';
import ModalPortal from '../../../../components/ModalPortal';

/**
 * ImportWizardModal - Modal for importing rules from delimited text or files
 * @param {Object} props - Component props
 * @param {boolean} props.show - Whether the modal is visible
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onImportComplete - Callback when import is complete
 * @param {Array} props.families - Array of family objects
 * @param {Array} props.featuresMetadata - Array of feature metadata
 * @param {string|number} props.activeFamilyId - Currently active family ID
 * @param {{id: string, name: string}|null} [props.targetRuleSet] - When set, import overwrites this ruleset and the name field is locked
 * @returns {JSX.Element|null} - Rendered modal or null if not shown
 */
export default function ImportWizardModal({ show, onClose, onImportComplete, families, featuresMetadata, activeFamilyId, targetRuleSet = null }) {
  const importOverlayMouseDown = useRef(false);
  const [ruleSetName, setRuleSetName] = useState('');
  const [selectedFamilyId, setSelectedFamilyId] = useState('');
  const [delimiter, setDelimiter] = useState('\t');
  const [hasHeaders, setHasHeaders] = useState(true);
  const [trimApostrophes, setTrimApostrophes] = useState(true);
  const [inputMode, setInputMode] = useState('paste'); // 'paste' | 'file'
  const [pastedData, setPastedData] = useState('');
  const [fileName, setFileName] = useState('');
  const [fileContent, setFileContent] = useState('');
  const [columnMappings, setColumnMappings] = useState([]);
  const [status, setStatus] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [showBlockingPopover, setShowBlockingPopover] = useState(false);

  // Clear data on open/close
  useEffect(() => {
    if (!show) {
      setRuleSetName('');
      setSelectedFamilyId('');
      setDelimiter('\t');
      setHasHeaders(true);
      setTrimApostrophes(true);
      setInputMode('paste');
      setPastedData('');
      setFileName('');
      setFileContent('');
      setColumnMappings([]);
      setStatus('');
      setShowBlockingPopover(false);
    } else {
      setSelectedFamilyId(activeFamilyId ? activeFamilyId.toString() : '');
      setRuleSetName(targetRuleSet?.name || '');
    }
  }, [show, activeFamilyId, targetRuleSet]);

  const rawData = inputMode === 'paste' ? pastedData : fileContent;

  // Robust custom parser supporting commas, semicolons, tabs, and pipes with quotes handling
  const parseDelimitedText = (text, delim) => {
    if (!text) return [];
    const actualDelim = delim === '\\t' || delim === '\t' ? '\t' : delim;
    const rows = [];
    let currentRow = [''];
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const nextChar = text[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentRow[currentRow.length - 1] += '"';
          i++; // skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === actualDelim && !inQuotes) {
        currentRow.push('');
      } else if ((char === '\r' || char === '\n') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          i++; // skip \n
        }
        rows.push(currentRow);
        currentRow = [''];
      } else {
        currentRow[currentRow.length - 1] += char;
      }
    }
    if (currentRow.length > 0 && (currentRow.length > 1 || currentRow[0] !== '')) {
      rows.push(currentRow);
    }
    // Filter empty rows
    return rows.filter(r => r.some(cell => cell.trim() !== ''));
  };

  const normalizeCell = (cell) => {
    const value = (cell ?? '').trim();
    if (!trimApostrophes) return value;
    return value
      .split(',')
      .map(part => part.trim().replace(/^'+|'+$/g, '').trim())
      .filter(Boolean)
      .join(', ');
  };

  const parsedRows = parseDelimitedText(rawData, delimiter).map(row => row.map(normalizeCell));
  const maxCols = parsedRows.reduce((max, r) => Math.max(max, r.length), 0);
  const columnIndices = Array.from({ length: maxCols }, (_, i) => i);

  // Synchronize column mappings when rawData or parsing settings change
  useEffect(() => {
    if (parsedRows.length > 0) {
      if (hasHeaders) {
        const firstRow = parsedRows[0] || [];
        const initialMappings = columnIndices.map((_, i) => firstRow[i]?.trim() || `Column_${i + 1}`);
        setColumnMappings(initialMappings);
      } else {
        setColumnMappings(prev => {
          const newMappings = [...prev];
          for (let i = 0; i < maxCols; i++) {
            if (newMappings[i] === undefined) {
              newMappings[i] = '';
            }
          }
          return newMappings.slice(0, maxCols);
        });
      }
    } else {
      setColumnMappings([]);
    }
  }, [rawData, delimiter, hasHeaders, maxCols, trimApostrophes]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      setFileContent(evt.target.result);
    };
    reader.readAsText(file);
  };

  const handleMappingChange = (index, value) => {
    setColumnMappings(prev => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  const isOverwrite = Boolean(targetRuleSet?.id);
  const missingName = !isOverwrite && !ruleSetName.trim();
  const missingData = !rawData.trim();
  const missingMappings = !hasHeaders && columnIndices.some(i => !columnMappings[i]?.trim());
  const blockingIssues = [];
  if (missingName) blockingIssues.push('Enter a rule set name');
  if (missingData) blockingIssues.push(inputMode === 'paste' ? 'Paste table data' : 'Upload a file');
  if (missingMappings) blockingIssues.push('Map every column to a feature name');
  const canImport = blockingIssues.length === 0 && !isImporting;

  const handleImport = async (e) => {
    e.preventDefault();
    if (!canImport) return;

    if (!hasHeaders) {
      const missingMappings = columnIndices.some(i => !columnMappings[i]?.trim());
      if (missingMappings) {
        setStatus('Error: Please specify feature names for all columns.');
        return;
      }
    }

    if (isOverwrite) {
      const confirmed = await window.confirm(
        `Are you sure you want to overwrite the existing combinations in '${targetRuleSet.name}'?`
      );
      if (!confirmed) return;
    }

    setIsImporting(true);
    setStatus('Importing...');

    try {
      const headers = columnMappings.map((name, i) => name?.trim() || `Column_${i + 1}`);
      const dataRows = hasHeaders ? parsedRows.slice(1) : parsedRows;
      
      const formattedRows = dataRows.map(row => 
        columnIndices.map(colIdx => row[colIdx]?.trim() || '').join('\t')
      );
      const headerLine = headers.join('\t');
      const finalTsv = [headerLine, ...formattedRows].join('\n');

      const res = isOverwrite
        ? await apiFetch(`/api/rulesets/${targetRuleSet.id}/import`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ TsvData: finalTsv })
          })
        : await apiFetch('/api/rulesets/import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              RuleSetName: ruleSetName.trim(),
              TsvData: finalTsv,
              FamilyId: selectedFamilyId || null
            })
          });

      if (res.ok) {
        const body = await res.json();
        setStatus(body.message);
        setTimeout(() => {
          onImportComplete();
          onClose();
        }, 1000);
      } else {
        const errorText = await res.text();
        setStatus(`Error: ${errorText}`);
      }
    } catch (err) {
      console.error(err);
      setStatus("Failed to connect to API");
    } finally {
      setIsImporting(false);
    }
  };

  const displayDataRows = hasHeaders ? parsedRows.slice(1, 11) : parsedRows.slice(0, 10);

  if (!show) return null;

  return (
    <ModalPortal>
    <div 
      className="modal-overlay fade-in" 
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) importOverlayMouseDown.current = true;
      }}
      onMouseUp={(e) => {
        if (e.target === e.currentTarget && importOverlayMouseDown.current) {
          onClose();
        }
        importOverlayMouseDown.current = false;
      }}
    >
      <div 
        className="modal-content" 
        onClick={e => e.stopPropagation()} 
        style={{ maxWidth: '800px', width: '90%', maxHeight: '95vh', overflowY: 'auto', borderRadius: 'var(--radius)' }}
      >
        <h2 style={{ color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
          <i className="fa-solid fa-wand-magic-sparkles" style={{ color: 'var(--accent)' }}></i>
          {isOverwrite ? 'Import (overwrite)' : 'Import Rules Wizard'}
        </h2>

        <form onSubmit={handleImport}>
          {/* Rule Set Name & Family Selection */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                Rule Set Name <span style={{ color: 'var(--error)' }}>*</span>
              </label>
              <input 
                type="text" 
                placeholder="e.g. exhaust_backpressure_matrix" 
                value={ruleSetName}
                onChange={e => setRuleSetName(e.target.value)} 
                className="input-field" 
                required={!isOverwrite}
                aria-required={!isOverwrite}
                disabled={isOverwrite}
                style={isOverwrite ? { background: 'var(--bg-secondary)', color: 'var(--text-secondary)' } : undefined}
              />
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--text-primary)' }}>Family</label>
              <input 
                type="text" 
                className="input-field" 
                value={families?.find(f => f.id.toString() === (activeFamilyId || '').toString())?.name || 'No Family Selected'} 
                disabled 
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}
              />
            </div>
          </div>

          {/* Delimiter & import options */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.25rem', fontSize: '0.9rem', color: 'var(--text-primary)' }}>Separator Delimiter</label>
              <select 
                className="input-field" 
                value={delimiter} 
                onChange={e => setDelimiter(e.target.value)}
              >
                <option value={'\t'}>Tab separated (\t)</option>
                <option value=",">Comma separated (,)</option>
                <option value=";">Semicolon separated (;)</option>
                <option value="|">Pipe separated (|)</option>
              </select>
            </div>
            <div style={{ flex: 1, minWidth: '200px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: '0.6rem', paddingTop: '1.2rem' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                <input 
                  type="checkbox" 
                  checked={hasHeaders} 
                  onChange={e => setHasHeaders(e.target.checked)} 
                  style={{ transform: 'scale(1.25)', cursor: 'pointer' }}
                />
                First row contains column headers
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                <input
                  type="checkbox"
                  checked={trimApostrophes}
                  onChange={e => setTrimApostrophes(e.target.checked)}
                  style={{ transform: 'scale(1.25)', cursor: 'pointer' }}
                />
                Trim apostrophes (')
              </label>
            </div>
          </div>

          {/* Source Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: '1rem' }}>
            <button 
              type="button"
              style={{ 
                padding: '0.5rem 1rem', 
                background: 'transparent', 
                border: 'none', 
                borderBottom: inputMode === 'paste' ? '2px solid var(--accent)' : 'none',
                color: inputMode === 'paste' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'var(--transition)'
              }}
              onClick={() => setInputMode('paste')}
            >
              Paste text
            </button>
            <button 
              type="button"
              style={{ 
                padding: '0.5rem 1rem', 
                background: 'transparent', 
                border: 'none', 
                borderBottom: inputMode === 'file' ? '2px solid var(--accent)' : 'none',
                color: inputMode === 'file' ? 'var(--text-primary)' : 'var(--text-secondary)',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'var(--transition)'
              }}
              onClick={() => setInputMode('file')}
            >
              Upload file
            </button>
          </div>

          {/* Source Panels */}
          {inputMode === 'paste' ? (
            <textarea 
              placeholder={(delimiter === '\t' || delimiter === '\\t') ? "Col1\tCol2\nVal1\tVal2" : "Col1,Col2\nVal1,Val2"}
              value={pastedData}
              onChange={e => setPastedData(e.target.value)} 
              className="textarea-field" 
              style={{ minHeight: '100px', fontFamily: 'monospace', fontSize: '0.85rem' }}
            />
          ) : (
            <div style={{ 
              border: '2px dashed var(--border)', 
              padding: '1.5rem', 
              borderRadius: '8px', 
              textAlign: 'center', 
              background: 'var(--bg-secondary)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <input 
                type="file" 
                accept=".csv,.tsv,.txt" 
                onChange={handleFileUpload} 
                style={{ display: 'none' }} 
                id="wizard-file-input" 
              />
              <label htmlFor="wizard-file-input" className="btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '8px 16px' }}>
                <i className="fa-solid fa-file-arrow-up"></i> Select file
              </label>
              {fileName ? (
                <span style={{ fontSize: '0.9rem', color: 'var(--text-primary)', fontWeight: '500' }}>
                  Selected file: <strong style={{ color: 'var(--accent)' }}>{fileName}</strong>
                </span>
              ) : (
                <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  Supports .csv, .tsv, and .txt files
                </span>
              )}
            </div>
          )}

          {/* Datalist for Auto-suggest */}
          <datalist id="wizard-existing-vars">
            {featuresMetadata?.map(v => (
              <option key={v.id} value={v.name} />
            ))}
          </datalist>

          {/* Column mappings & Preview Grid */}
          {parsedRows.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem' }}>
                <i className="fa-solid fa-magnifying-glass" style={{ color: 'var(--accent)' }}></i>
                Data Preview (First 10 rows)
              </h4>
              <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: '8px', maxHeight: '300px' }}>
                <table className="data-grid" style={{ margin: 0, width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-secondary)', zIndex: 10 }}>
                    <tr>
                      {columnIndices.map(colIdx => (
                        <th key={colIdx} style={{ padding: '10px 8px', background: 'var(--bg-secondary)', borderBottom: '2px solid var(--border)', minWidth: '180px', borderRight: '1px solid var(--border)' }}>
                          {hasHeaders ? (
                            <div style={{ fontWeight: '700', color: 'var(--text-primary)', fontSize: '0.85rem', textAlign: 'center' }}>
                              {columnMappings[colIdx] || `Column ${colIdx + 1}`}
                            </div>
                          ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', textAlign: 'left' }}>
                              <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 'bold' }}>Map to Feature:</span>
                              <input 
                                type="text" 
                                list="wizard-existing-vars" 
                                value={columnMappings[colIdx] || ''} 
                                onChange={e => handleMappingChange(colIdx, e.target.value)} 
                                className="input-field"
                                placeholder={`Var name...`} 
                                style={{ padding: '4px 8px', fontSize: '0.85rem' }} 
                                required
                              />
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayDataRows.map((row, rowIdx) => (
                      <tr key={rowIdx}>
                        {columnIndices.map(colIdx => (
                          <td key={colIdx} style={{ padding: '8px', fontSize: '0.8rem', color: 'var(--text-primary)', fontFamily: 'monospace', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
                            {row[colIdx] !== undefined ? row[colIdx] : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Modal Actions & Status */}
          <div className="modal-actions" style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '1rem' }}>
            {status && (
              <span style={{
                marginRight: 'auto', 
                fontSize: '0.85rem', 
                color: status.toLowerCase().includes('error') ? 'var(--error)' : 'var(--accent)', 
                fontWeight: '600' 
              }}>
                {status}
              </span>
            )}
            <button type="button" onClick={onClose} className="btn-secondary" disabled={isImporting} style={{ padding: '8px 16px' }}>Cancel</button>
            <span
              style={{ position: 'relative', display: 'inline-flex' }}
              onMouseEnter={() => {
                if (blockingIssues.length > 0 && !isImporting) setShowBlockingPopover(true);
              }}
              onMouseLeave={() => setShowBlockingPopover(false)}
            >
              {showBlockingPopover && blockingIssues.length > 0 && (
                <div
                  id="import-table-blocking-hint"
                  role="tooltip"
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    right: 0,
                    minWidth: '220px',
                    maxWidth: '280px',
                    padding: '0.75rem 0.9rem',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    background: 'var(--bg-card)',
                    boxShadow: '0 14px 28px rgba(15, 23, 42, 0.16)',
                    zIndex: 50,
                    pointerEvents: 'none'
                  }}
                >
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.4rem' }}>
                    To import this table
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '1.1rem', fontSize: '0.8rem', color: 'var(--error)', fontWeight: 500, lineHeight: 1.45 }}>
                    {blockingIssues.map(issue => (
                      <li key={issue}>{issue}</li>
                    ))}
                  </ul>
                </div>
              )}
              <button 
                type="submit" 
                className="btn-primary" 
                style={{ padding: '8px 16px' }}
                disabled={!canImport}
                aria-describedby={blockingIssues.length > 0 ? 'import-table-blocking-hint' : undefined}
              >
                {isImporting ? "Importing..." : "Import Table"}
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
    </ModalPortal>
  );
}
